import { useCallback, useState } from 'react';
import { Button, ButtonSizes, ButtonVariants, Form, FormSizes } from '@carboledger/ui';
import { isFormDirty } from '@/lib/utils';
import { ConfirmDialog } from '@/components/feedback';
import { TextField, GeneratedIdField } from '@/components/common';
import { useTranslations } from '@/hooks';
import { SITE_FIELDS } from '../../constants/sites.constants';
import { SiteCertificateFields } from './SiteCertificateFields';
import { SiteCountryField } from './SiteCountryField';
import { SiteTerminalFields } from './SiteTerminalFields';
import styles from './SiteForm.module.scss';

/**
 * Props:
 *   title · initialValues · onSubmit · onClose · isSubmitting
 *   isEdit  locks the site ID — PATCH /sites/:id cannot change it — and holds Save until a change
 */
export const SiteForm = ({
  title,
  initialValues,
  onSubmit,
  onClose,
  isSubmitting = false,
  isEdit = false,
}) => {
  const t = useTranslations();

  const [confirmingClose, setConfirmingClose] = useState(false);
  const askBeforeClosing = useCallback(() => setConfirmingClose(true), []);
  const keepEditing = useCallback(() => setConfirmingClose(false), []);

  return (
    <Form
      size={FormSizes.large}
      initialValues={initialValues}
      onSubmit={onSubmit}
      className={styles.form}
    >
      {({ values }) => {
        const isDirty = isFormDirty(values, initialValues);
        const requestClose = isDirty ? askBeforeClosing : onClose;
        return (
          <>
            <Form.Header title={title} onClose={requestClose} closeLabel={t('COMMON_CLOSE')} />

            <Form.Section className={styles.fields}>
              <Form.Row columns={3}>
                <TextField
                  required
                  name={SITE_FIELDS.siteName}
                  label={t('FIELD_SITE_NAME')}
                  placeholder={t('FORM_SITE_NAME_PLACEHOLDER')}
                />
                <TextField
                  required
                  name={SITE_FIELDS.siteDeclarationName}
                  label={t('FIELD_OFFICIAL_SITE_NAME')}
                  placeholder={t('FORM_OFFICIAL_SITE_NAME_PLACEHOLDER')}
                />
                <GeneratedIdField
                  name={SITE_FIELDS.siteId}
                  label={t('FIELD_SITE_ID')}
                  placeholder={t('FORM_SITE_ID_PLACEHOLDER')}
                  readOnly={isEdit}
                />
              </Form.Row>
            </Form.Section>

            <Form.Section title={t('SECTION_SHIPPING_ADDRESS')} className={styles.fields}>
              <Form.Row columns={3}>
                <TextField
                  required
                  className={styles.wide}
                  name={SITE_FIELDS.addressLine1}
                  label={t('FORM_ADDRESS_LINE_1')}
                  placeholder={t('FORM_ADDRESS_LINE_1_PLACEHOLDER')}
                />
                <TextField
                  name={SITE_FIELDS.addressLine2}
                  label={t('FORM_ADDRESS_LINE_2')}
                  placeholder={t('FORM_ADDRESS_LINE_2_PLACEHOLDER')}
                />
              </Form.Row>

              <Form.Row columns={3}>
                <TextField
                  required
                  name={SITE_FIELDS.city}
                  label={t('FORM_CITY_NAME')}
                  placeholder={t('FORM_CITY_NAME_PLACEHOLDER')}
                />
                <SiteCountryField name={SITE_FIELDS.country} />
                <TextField
                  required
                  name={SITE_FIELDS.zipCode}
                  label={t('FORM_ZIP_CODE')}
                  placeholder={t('FORM_ZIP_CODE_PLACEHOLDER')}
                />
              </Form.Row>
            </Form.Section>

            <Form.Section title={t('SECTION_CERTIFICATE_DETAILS')} className={styles.fields}>
              <SiteCertificateFields />
            </Form.Section>

            <Form.Section title={t('SECTION_TERMINAL_DETAILS')} className={styles.fields}>
              <SiteTerminalFields />
            </Form.Section>

            <Form.Footer>
              <Button variant={ButtonVariants.neutral} size={ButtonSizes.sm} onClick={requestClose}>
                {t('COMMON_CANCEL')}
              </Button>
              <Button
                type="submit"
                variant={ButtonVariants.primary}
                size={ButtonSizes.sm}
                disabled={isSubmitting || (isEdit && !isDirty)}
              >
                {t('COMMON_SAVE')}
              </Button>
            </Form.Footer>

            <ConfirmDialog
              open={confirmingClose}
              title={t('COMMON_DISCARD_TITLE')}
              bodyTitle={t('COMMON_DISCARD_BODY_TITLE')}
              description={t('COMMON_DISCARD_DESCRIPTION')}
              confirmLabel={t('COMMON_YES')}
              onConfirm={onClose}
              onCancel={keepEditing}
            />
          </>
        );
      }}
    </Form>
  );
};
